import { useMemo, useState } from 'react';

export type DealStageFilter =
  | 'all'
  | 'kunde-wartet'
  | 'projekt-angenommen'
  | 'projekt-abgeschlossen'
  | 'durchfuehrung-abgeschlossen'
  | 'waiting'
  | 'dubletten'
  | 'anfrage-zum-kennenlernen-verschicken';

export type NacharbeitenFilter = 'all' | 'yes' | 'no';
export type InterneFreigabeFilter = 'all' | 'empty_or_draft' | 'ready' | 'in_review' | 'approved';

interface DealStatusFilterBarProps {
  dealStageFilter: DealStageFilter;
  nacharbeitenFilter: NacharbeitenFilter;
  interneFreigabeFilter: InterneFreigabeFilter;
  onDealStageFilterChange: (value: DealStageFilter) => void;
  onNacharbeitenFilterChange: (value: NacharbeitenFilter) => void;
  onInterneFreigabeFilterChange: (value: InterneFreigabeFilter) => void;
  onReset: () => void;
  onRefresh: () => void;
  disabled?: boolean;
}

export default function DealStatusFilterBar(props: DealStatusFilterBarProps) {
  const {
    dealStageFilter,
    nacharbeitenFilter,
    interneFreigabeFilter,
    onDealStageFilterChange,
    onNacharbeitenFilterChange,
    onInterneFreigabeFilterChange,
    onReset,
    onRefresh,
    disabled = false,
  } = props;
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (dealStageFilter !== 'all') count += 1;
    if (nacharbeitenFilter !== 'all') count += 1;
    if (interneFreigabeFilter !== 'all') count += 1;
    return count;
  }, [dealStageFilter, interneFreigabeFilter, nacharbeitenFilter]);

  return (
    <section className="rounded-[28px] border border-slate-200/70 bg-white/95 p-5 shadow-[0_20px_70px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-slate-900/80 dark:shadow-[0_20px_70px_rgba(2,6,23,0.45)] sm:p-6">
      <div className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Tabellenfilter</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">Filtere direkt auf die gewünschten Deal-Status aus HubSpot.</p>
          </div>
          <button
            type="button"
            onClick={() => setMobileFiltersOpen((current) => !current)}
            className="inline-flex min-h-[40px] items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900 dark:border-white/15 dark:bg-slate-950/80 dark:text-slate-200 dark:hover:border-white/30 sm:hidden"
            aria-expanded={mobileFiltersOpen}
            aria-controls="deal-status-mobile-filters"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 5h18l-7 8v5l-4 2v-7L3 5z" />
            </svg>
            <span>Filter</span>
            <span className="text-slate-500 dark:text-slate-400">
              {activeFilterCount > 0 ? `${activeFilterCount} aktiv` : 'Alle'}
            </span>
          </button>
        </div>
        <div
          id="deal-status-mobile-filters"
          className={`${mobileFiltersOpen ? 'grid' : 'hidden'} gap-4 sm:grid lg:grid-cols-[1fr_1fr_1fr_auto_auto]`}
        >
          <label className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Deal-Status</span>
            <select
              value={dealStageFilter}
              disabled={disabled}
              onChange={(event) => onDealStageFilterChange(event.target.value as DealStageFilter)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition focus:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-slate-950/80 dark:text-slate-100 dark:focus:border-white/20"
            >
              <option value="all">Alle</option>
              <option value="kunde-wartet">Kunde wartet</option>
              <option value="projekt-angenommen">Projekt Angenommen</option>
              <option value="projekt-abgeschlossen">Projekt Abgeschlossen</option>
              <option value="durchfuehrung-abgeschlossen">Durchführung abgeschlossen</option>
              <option value="waiting">waiting</option>
              <option value="dubletten">Dubletten</option>
              <option value="anfrage-zum-kennenlernen-verschicken">Anfrage zum Kennenlernen verschicken</option>
            </select>
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Nacharbeiten</span>
            <select
              value={nacharbeitenFilter}
              disabled={disabled}
              onChange={(event) => onNacharbeitenFilterChange(event.target.value as NacharbeitenFilter)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition focus:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-slate-950/80 dark:text-slate-100 dark:focus:border-white/20"
            >
              <option value="all">Alle</option>
              <option value="yes">Ja</option>
              <option value="no">Nein</option>
            </select>
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Interne Freigabe</span>
            <select
              value={interneFreigabeFilter}
              disabled={disabled}
              onChange={(event) => onInterneFreigabeFilterChange(event.target.value as InterneFreigabeFilter)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition focus:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-slate-950/80 dark:text-slate-100 dark:focus:border-white/20"
            >
              <option value="all">Alle</option>
              <option value="empty_or_draft">Leer oder Entwurf</option>
              <option value="ready">Freigabefähig</option>
              <option value="in_review">In Prüfung</option>
              <option value="approved">Freigegeben</option>
            </select>
          </label>
          <div className="flex items-end">
            <button
              type="button"
              disabled={disabled}
              title="Lädt die Daten ohne Cache neu aus HubSpot."
              onClick={onRefresh}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/15 dark:bg-slate-950/80 dark:text-slate-200 dark:hover:bg-white/5 lg:w-auto"
            >
              Aktualisieren
            </button>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              disabled={disabled}
              onClick={onReset}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5 lg:w-auto"
            >
              Filter zurücksetzen
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
